import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { getUserData } from "../../../services/apiService";
import {
  readStoredSignature,
  saveSignatureForCurrentUser,
  typedSignatureToDataUrl,
} from "./esignatureUtils";

/**
 * eISF-style e-signature confirm + draw/type capture.
 * Used before authenticated actions that require a saved staff signature.
 */
export default function ESignatureConfirmModal({
  show,
  onClose,
  onConfirm,
  title = "Confirm e-signature",
  confirmQuestion = "",
  legalText = "In accordance with 21 CFR Part 11 of the Code of Federal Regulations this is to certify that this electronic signature is to be the legally binding equivalent of my signature.",
  confirmButtonText = "Yes",
  cancelButtonText = "No",
}) {
  const userData = getUserData() || {};
  const [sessionSignature, setSessionSignature] = useState(null);
  const [showDrawModal, setShowDrawModal] = useState(false);
  const [signatureMode, setSignatureMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const signatureCanvasRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setSessionSignature(readStoredSignature(userData));
    setShowDrawModal(false);
    setSaveError("");
    setTypedSignature("");
    setSignatureMode("draw");
  }, [show, userData?.StaffId, userData?.SignatureBase64]);

  if (!show) return null;

  const openDrawModal = () => {
    setSaveError("");
    setTypedSignature("");
    setSignatureMode("draw");
    setShowDrawModal(true);
    setTimeout(() => signatureCanvasRef.current?.clear?.(), 50);
  };

  const handleSaveSignature = async () => {
    let dataUrl = null;
    if (signatureMode === "draw") {
      if (signatureCanvasRef.current?.isEmpty?.()) {
        setSaveError("Please draw your signature before saving.");
        return;
      }
      dataUrl = signatureCanvasRef.current.toDataURL("image/png");
    } else {
      dataUrl = typedSignatureToDataUrl(typedSignature);
      if (!dataUrl) {
        setSaveError("Please type your signature before saving.");
        return;
      }
    }

    setSaving(true);
    setSaveError("");
    const result = await saveSignatureForCurrentUser(dataUrl);
    setSaving(false);
    if (!result.success) {
      setSaveError(result.message || "Failed to save signature.");
      return;
    }
    setSessionSignature(result.signature);
    setShowDrawModal(false);
    setTypedSignature("");
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 12050 }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12">{legalText}</div>
                <div className="col-md-12 mt-3">
                  {sessionSignature ? (
                    <div className="d-flex flex-column align-items-start">
                      <label className="small text-secondary mb-1">
                        Your e-signature on file
                      </label>
                      <img
                        src={sessionSignature}
                        alt="E-signature"
                        style={{
                          maxWidth: "240px",
                          maxHeight: "100px",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          padding: "4px",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link btn-sm px-0 mt-1"
                        onClick={openDrawModal}
                      >
                        Update signature
                      </button>
                    </div>
                  ) : (
                    <div className="alert alert-warning py-2 mb-0">
                      <div className="d-flex flex-column align-items-center flex-sm-row justify-content-between gap-2">
                        <span>
                          You do not have an e-signature saved yet. Please add
                          your signature before continuing.
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary text-nowrap"
                          onClick={openDrawModal}
                        >
                          Add signature
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {confirmQuestion ? (
                  <div className="col-md-12 mt-3">{confirmQuestion}</div>
                ) : null}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                {cancelButtonText}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!sessionSignature}
                onClick={() =>
                  onConfirm?.({
                    SignatureBase64: sessionSignature,
                    success: true,
                    Success: 1,
                  })
                }
              >
                {confirmButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showDrawModal ? (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 12060 }}
          role="dialog"
          aria-modal="true"
          aria-label="Draw your signature"
          onClick={(e) =>
            e.target === e.currentTarget && !saving && setShowDrawModal(false)
          }
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-pen-fancy me-2" aria-hidden="true" />
                  E-Signature
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  disabled={saving}
                  onClick={() => setShowDrawModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <button
                    type="button"
                    className={`btn btn-sm me-2 ${
                      signatureMode === "draw"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setSignatureMode("draw")}
                    disabled={saving}
                  >
                    <i className="fas fa-pen me-1" aria-hidden="true" />
                    Draw
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      signatureMode === "type"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setSignatureMode("type")}
                    disabled={saving}
                  >
                    <i className="fas fa-keyboard me-1" aria-hidden="true" />
                    Type
                  </button>
                </div>
                {signatureMode === "draw" ? (
                  <>
                    <p className="text-muted small mb-2">
                      Draw your signature in the box below.
                    </p>
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "4px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <SignatureCanvas
                        ref={signatureCanvasRef}
                        canvasProps={{
                          style: {
                            width: "100%",
                            height: "160px",
                          },
                        }}
                        penColor="#1a1a1a"
                        backgroundColor="#f8f9fa"
                        clearOnResize={false}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      Type your signature in the box below.
                    </p>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. John Smith"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      aria-label="Type your signature"
                      disabled={saving}
                    />
                    {typedSignature.trim() ? (
                      <div className="mt-2">
                        <span
                          style={{
                            fontFamily:
                              '"Dancing Script", "Brush Script MT", "Segoe Script", cursive',
                            fontSize: "32px",
                            fontStyle: "italic",
                          }}
                        >
                          {typedSignature.trim()}
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
                {saveError ? (
                  <p className="text-danger small mt-2 mb-0">{saveError}</p>
                ) : null}
                <div className="mt-3 d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={saving}
                    onClick={() => {
                      if (signatureMode === "draw") {
                        signatureCanvasRef.current?.clear?.();
                      } else {
                        setTypedSignature("");
                      }
                      setSaveError("");
                    }}
                  >
                    Clear
                  </button>
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      disabled={saving}
                      onClick={handleSaveSignature}
                    >
                      {saving ? "Saving…" : "Save signature"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={saving}
                      onClick={() => setShowDrawModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
